import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ImageCarousel.css';

export type LoopCount = number | 'infinite';

export interface CarouselSlide {
  src: string;
  alt: string;
  type?: 'image' | 'video';
  /** Shown while the video loads / before playback. */
  poster?: string;
  /** Defaults to 1. Use 'infinite' to stay until the user changes slides. */
  loopCount?: LoopCount;
}

interface ImageCarouselProps {
  slides: CarouselSlide[];
  /** Photo display duration for one "loop" (ms). Default 5000. */
  interval?: number;
}

function resolveType(slide: CarouselSlide): 'image' | 'video' {
  if (slide.type) return slide.type;
  return /\.(mp4|webm|ogg)(\?|$)/i.test(slide.src) ? 'video' : 'image';
}

function resolveLoopCount(slide: CarouselSlide): LoopCount {
  return slide.loopCount ?? 1;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  slides,
  interval = 5000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const photoLoopsDone = useRef(0);
  const videoLoopsDone = useRef(0);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  // Reset loop counters and manage media when the active slide changes
  useEffect(() => {
    photoLoopsDone.current = 0;
    videoLoopsDone.current = 0;

    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (!video) return;
      const index = Number(key);
      if (index === currentIndex) {
        video.currentTime = 0;
        void video.play().catch(() => {
          // Autoplay may be blocked; muted + playsInline usually allows it
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [currentIndex, slides]);

  // Photo auto-advance based on loopCount
  useEffect(() => {
    if (slides.length <= 1) return;

    const slide = slides[currentIndex];
    if (resolveType(slide) !== 'image') return;

    const loopCount = resolveLoopCount(slide);
    if (loopCount === 'infinite') return;

    const timer = setInterval(() => {
      photoLoopsDone.current += 1;
      if (photoLoopsDone.current >= loopCount) {
        goToNext();
      }
    }, interval);

    return () => clearInterval(timer);
  }, [currentIndex, slides, interval, goToNext]);

  const handleVideoEnded = (index: number) => {
    if (index !== currentIndex) return;

    const slide = slides[index];
    const loopCount = resolveLoopCount(slide);

    if (loopCount === 'infinite') {
      const video = videoRefs.current[index];
      if (video) {
        video.currentTime = 0;
        void video.play().catch(() => {});
      }
      return;
    }

    videoLoopsDone.current += 1;
    if (videoLoopsDone.current >= loopCount) {
      goToNext();
      return;
    }

    const video = videoRefs.current[index];
    if (video) {
      video.currentTime = 0;
      void video.play().catch(() => {});
    }
  };

  if (slides.length === 0) return null;

  return (
    <div className="image-carousel">
      <div className="carousel-container">
        <button
          className="carousel-button carousel-button-prev"
          onClick={goToPrevious}
          aria-label="Previous slide"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="carousel-slides">
          {slides.map((slide, index) => {
            const mediaType = resolveType(slide);
            const loopCount = resolveLoopCount(slide);
            const isInfinite = loopCount === 'infinite';

            return (
              <div
                key={`${slide.src}-${index}`}
                className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {mediaType === 'video' ? (
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    className="carousel-media"
                    src={slide.src}
                    poster={slide.poster}
                    muted
                    playsInline
                    preload="metadata"
                    autoPlay={index === currentIndex}
                    loop={isInfinite}
                    onEnded={() => handleVideoEnded(index)}
                    aria-label={slide.alt}
                  />
                ) : (
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="carousel-media"
                  />
                )}
              </div>
            );
          })}
        </div>

        <button
          className="carousel-button carousel-button-next"
          onClick={goToNext}
          aria-label="Next slide"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {slides.length > 1 && (
        <div className="carousel-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
